/*  
This component prints the filtered data based on account, cluster, namespace based on which the image and its version are shown. This component 
is called from Home.js file and rendered as part of service inventory page.

This component has table heading and table data. It also has sort function (sorts based on arrow, when clicked ▲ it sorts that column in 
ascending order. When clicked ▼ it sorts that column based on descending order) implemeted along with draggable function (We can increase 
or decrease the size of table column by just dragging it).
*/

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import './Table.css';
import Pagination from './Pagination';
import { ChevronDown, ChevronRight } from 'lucide-react';
import loader from "../../assets/loader.gif";

const DynamicTable = ({ searchTerm, selectedClusters, selectedCluster, selectedNamespaces, environmentData, loading, error }) => {
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState({});
  const [columns, setColumns] = useState([
    { key: 'deploymentName', label: 'Deployment Name', width: 19 },
    { key: 'namespace', label: 'Namespace', width: 15 },
    { key: 'clusterName', label: 'Cluster Name', width: 15 },
    { key: 'mainImage', label: 'Main Container Images', width: 43 },
    { key: 'version', label: 'Version', width: 10 },
    { key: 'image_tag', label: 'Image Tag', width: 15 },
  ]);

  const tableRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [currentResizer, setCurrentResizer] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCluster, selectedNamespaces, environmentData]);

  const toggleRow = (rowId) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handleItemsPerPageChange = (newValue) => {
    setItemsPerPage(newValue);
    setCurrentPage(1);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reorderedColumns = Array.from(columns);
    const [movedColumn] = reorderedColumns.splice(result.source.index, 1);
    reorderedColumns.splice(result.destination.index, 0, movedColumn);
    setColumns(reorderedColumns);
  };

  const startResizing = useCallback((e, index) => {
    setIsResizing(true);
    setCurrentResizer(index);
    setStartX(e.clientX);
    const headers = tableRef.current.getElementsByTagName('th');
    setStartWidth(headers[index].offsetWidth);
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
    setCurrentResizer(null);
  }, []);

  const resize = useCallback((e) => {
    if (!isResizing || currentResizer === null || !tableRef.current) return;
    const diff = e.clientX - startX;
    const newWidth = Math.max(50, startWidth + diff);
    const totalWidth = tableRef.current.offsetWidth;
    const newPercentage = (newWidth / totalWidth) * 100;
    setColumns(prevColumns => {
      const newColumns = [...prevColumns];
      newColumns[currentResizer] = {
        ...newColumns[currentResizer],
        width: newPercentage
      };
      return newColumns;
    });
  }, [isResizing, currentResizer, startX, startWidth]);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', resize);
      window.addEventListener('mouseup', stopResizing);
      return () => {
        window.removeEventListener('mousemove', resize);
        window.removeEventListener('mouseup', stopResizing);
      };
    }
  }, [isResizing, resize, stopResizing]);

  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm) return text;

    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return text.replace(regex, '<span class="highlight">$1</span>');
  };

  const renderCell = (containers, type, isExpanded, searchTerm) => {
    if (!containers || containers.length === 0) return 'N/A';

    if (containers.length === 1 || !isExpanded) {
      const container = containers[0];
      let value;
      switch (type) {
        case 'image':
          value = container.image?.split(':')[0] || 'N/A';
          break;
        case 'version':
          value = container.version || 'N/A';
          break;
        case 'image_tag':
          value = container.image_tag || 'N/A';
          break;
        default:
          value = 'N/A';
      }
      return <span dangerouslySetInnerHTML={{ __html: highlightSearchTerm(value, searchTerm) }} />;
    }

    return (
      <div className="multi-container">
        {containers.map((container, idx) => {
          const value = type === 'image' ? container.image?.split(':')[0] :
                       type === 'version' ? container.version :
                       type === 'image_tag' ? container.image_tag : '';

          return (
            <React.Fragment key={idx}>
              <span dangerouslySetInnerHTML={{ __html: highlightSearchTerm(value || 'N/A', searchTerm) }} />
              {idx < containers.length - 1 && (
                <>
                  ,
                  <br />
                </>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const getSortValue = (item, key) => {
    switch (key) {
      case 'deploymentName':
        return item['deployment-name'] || '';
      case 'namespace':
        return item.namespace || '';
      case 'clusterName':
        return item.cluster || '';
      case 'mainImage':
        return item['main-containers']?.[0]?.image?.split(':')[0] || '';
      case 'version':
        return item['main-containers']?.[0]?.version || '';
      case 'image_tag':
        return item['main-containers']?.[0]?.image_tag || '';
      default:
        return '';
    }
  };

  const sortedData = [...environmentData].sort((a, b) => {
    if (!sortConfig.key) return 0;

    const aValue = getSortValue(a, sortConfig.key);
    const bValue = getSortValue(b, sortConfig.key);

    if (sortConfig.key === 'version') {
      const aNum = parseFloat(aValue) || 0;
      const bNum = parseFloat(bValue) || 0;
      return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
    }

    if (sortConfig.direction === 'asc') {
      return aValue.localeCompare(bValue, undefined, { numeric: true });
    }
    return bValue.localeCompare(aValue, undefined, { numeric: true });
  });

  const filteredData = sortedData
    .filter((item) => {
      return selectedClusters.length === 0 || selectedClusters.includes(item.cluster);
    })
    .filter((item) => {
      const search = (field) =>
        field &&
        typeof field === 'string' &&
        field.toLowerCase().includes(searchTerm.toLowerCase());
      const namespaceMatch = selectedNamespaces.length === 0 || selectedNamespaces.includes(item.namespace);
      return (
        (searchTerm === '' ||
          search(item['deployment-name']) ||
          search(item.namespace) ||
          search(item.cluster) ||
          item['main-containers']?.some((container) =>
            container.image.toLowerCase().includes(searchTerm.toLowerCase())
          )) &&
        namespaceMatch
      );
    });

  const totalItems = filteredData.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredData.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="table-container">
      <DragDropContext onDragEnd={onDragEnd}>
        <table className="table-dashboard" ref={tableRef}>
          <thead>
            <Droppable droppableId="columns" direction="horizontal" type="column">
              {(provided) => (
                <tr {...provided.droppableProps} ref={provided.innerRef}>
                  {columns.map((column, index) => (
                    <Draggable
                      key={column.key}
                      draggableId={column.key.toString()}
                      index={index}
                    >
                      {(provided) => (
                        <th
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="draggable-header"
                          style={{
                            ...provided.draggableProps.style,
                            width: `${column.width}%`
                          }}
                        >
                          <div className="table-header-content">
                            <span className="table-heading">{column.label}</span>
                            <div className="sort-arrows">
                              <span
                                onClick={() => handleSort(column.key, 'asc')}
                                className={`arrow ${sortConfig.key === column.key && sortConfig.direction === 'asc' ? 'active' : ''}`}
                              >
                                ▲
                              </span>
                              <span
                                onClick={() => handleSort(column.key, 'desc')}
                                className={`arrow ${sortConfig.key === column.key && sortConfig.direction === 'desc' ? 'active' : ''}`}
                              >
                                ▼
                              </span>
                            </div>
                          </div>
                          <div
                            className="resizer"
                            onMouseDown={(e) => startResizing(e, index)}
                          />
                        </th>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </tr>
              )}
            </Droppable>
          </thead>
          <tbody className='tbody-home'>
            {loading && (
              <tr>
                <td colSpan={columns.length} className="status-message">
                  <div className="loading">
                      <img src={loader} alt="Loading..." />
                  </div>
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan={columns.length} className="status-message-error">Error: {error}</td>
              </tr>
            )}
            {!loading && !error && currentItems.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="status-message">No data found</td>
              </tr>
            )}
            {!loading && !error && currentItems.map((item, index) => {
              const mainContainers = item['main-containers'] || [];
              const isExpanded = expandedRows[index];
              const hasMultipleContainers = mainContainers.length > 1;
              return (
                <tr key={index}>
                  <td style={{ width: `${columns[0].width}%` }}>
                    <div className="name-cell">
                      <button
                        onClick={() => hasMultipleContainers && toggleRow(index)}
                        className={`button-toggle ${!hasMultipleContainers ? 'button-disabled' : ''}`}
                        disabled={!hasMultipleContainers}
                      >
                        {isExpanded ?
                          <ChevronDown size={16} className={`chevron-icon ${!hasMultipleContainers ? 'disabled' : ''}`} /> :
                          <ChevronRight size={16} className={`chevron-icon ${!hasMultipleContainers ? 'disabled' : ''}`} />
                        }
                      </button>
                      <span className="deployment-name" dangerouslySetInnerHTML={{ __html: highlightSearchTerm(item['deployment-name'], searchTerm) }} />
                    </div>
                  </td>
                  <td style={{ width: `${columns[1].width}%` }} dangerouslySetInnerHTML={{ __html: highlightSearchTerm(item.namespace || 'N/A', searchTerm) }} />
                  <td style={{ width: `${columns[2].width}%` }} dangerouslySetInnerHTML={{ __html: highlightSearchTerm(item.cluster || 'Unknown', searchTerm) }} />
                  <td style={{ width: `${columns[3].width}%` }}>{renderCell(mainContainers, 'image', isExpanded, searchTerm)}</td>
                  <td style={{ width: `${columns[4].width}%` }}>{renderCell(mainContainers, 'version', isExpanded, searchTerm)}</td>
                  <td style={{ width: `${columns[5].width}%` }}>{renderCell(mainContainers, 'image_tag', isExpanded, searchTerm)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </DragDropContext>
      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}
    </div>
  );
};

export default DynamicTable;
